import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

function TeamPage() {
  const { abbreviation } = useParams();
  const [team, setTeam] = useState(null);
  const [record, setRecord] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [roster, setRoster] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [leaders, setLeaders] = useState({
    pass: null,
    rush: null,
    rec: null,
    tackles: null,
  });
  const [activeTab, setActiveTab] = useState("stats");
  const [loading, setLoading] = useState(true);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [scheduleLoaded, setScheduleLoaded] = useState(false);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

// Navbar dropdowns
  const [openDropdown, setOpenDropdown] = useState(null); // "teams" | "leaders" | "players" | null
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "teams" | "leaders" | "players" | null
  
  // Navbar search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

// Close search + any dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e) => {
    // Close expanded search
    if (
      navbarContainerRef.current &&
      !navbarContainerRef.current.contains(e.target)
    ) {
      setSearchExpanded(false);
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }

    // Close dropdowns
    if (
      teamsRef.current && !teamsRef.current.contains(e.target) &&
      leadersRef.current && !leadersRef.current.contains(e.target) &&
      playersRef.current && !playersRef.current.contains(e.target)
    ) {
      setOpenDropdown(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ========== Navbar Search ==========
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            navbarSearchTerm
          )}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  // Close navbar results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navbarContainerRef.current &&
        !navbarContainerRef.current.contains(e.target)
      ) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// Keyboard navigation – Navbar
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!navbarResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) =>
        Math.min(prev + 1, navbarResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
      const selected = navbarResults[navbarSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setNavbarSearchTerm("");
      setNavbarResults([]);
    } else if (e.key === "Escape") {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [navbarResults, navbarSelectedIndex]);

  // Load core data on mount
  useEffect(() => {
    const fetchCore = async () => {
      if (!abbreviation) return;

      try {
        const abbr = abbreviation.toUpperCase();

        const [
          teamRes,
          recordRes,
          statsRes,
          passRes,
          rushRes,
          recRes,
          tacklesRes,
        ] = await Promise.all([
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team?abbr=${abbr}`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-record?abbr=${abbr}`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-season-stats?abbr=${abbr}`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-season-leaders?abbr=${abbr}&stat=passing_yards`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-season-leaders?abbr=${abbr}&stat=rushing_yards`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-season-leaders?abbr=${abbr}&stat=receiving_yards`, AUTH),
          axios.get(`https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-season-leaders?abbr=${abbr}&stat=solo_tackles`, AUTH),
        ]);

        setTeam(teamRes.data.team);
        setRecord(recordRes.data);
        setTeamStats(statsRes.data);
        setLeaders({
          pass: passRes.data?.[0] || passRes.data || null,
          rush: rushRes.data?.[0] || rushRes.data || null,
          rec: recRes.data?.[0] || recRes.data || null,
          tackles: tacklesRes.data?.[0] || tacklesRes.data || null,
        });
      } catch (err) {
        console.error("TeamPage core error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCore();
  }, [abbreviation]);

  // Lazy load Roster
  useEffect(() => {
    if (activeTab !== "roster" || rosterLoaded || !abbreviation) return;

    const fetchRoster = async () => {
      setLoadingRoster(true);
      try {
        const abbr = abbreviation.toUpperCase();
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-roster?abbr=${abbr}&active=true`,
          AUTH
        );
        setRoster(res.data || []);
        setRosterLoaded(true);
      } catch (err) {
        console.error("Roster error:", err);
      } finally {
        setLoadingRoster(false);
      }
    };

    fetchRoster();
  }, [activeTab, abbreviation, rosterLoaded]);

  // Lazy load Schedule
  useEffect(() => {
    if (activeTab !== "schedule" || scheduleLoaded || !abbreviation) return;

    const fetchSchedule = async () => {
      setLoadingSchedule(true);
      try {
        const abbr = abbreviation.toUpperCase();
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-team-schedule?abbr=${abbr}`,
          AUTH
        );
        setSchedule(res.data || []);
        setScheduleLoaded(true);
      } catch (err) {
        console.error("Schedule error:", err);
      } finally {
        setLoadingSchedule(false);
      }
    };

    fetchSchedule();
  }, [activeTab, abbreviation, scheduleLoaded]);

  if (loading)
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-white">
        Loading team...
      </div>
    );

  if (!team)
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-red-500">
        Team not found
      </div>
    );

  const sortedRoster = [...roster].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/HOFLogoSquare.png"
              alt="HOF Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-2xl font-black tracking-tighter">
                HELMETS OFF
              </div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block"></div>

{/* Right side */}
<div className="flex items-center gap-4">
  
  {/* Collapsible Search (works on all screen sizes) */}
  <div className="relative" ref={navbarContainerRef}>
    {!searchExpanded ? (
      <button
        onClick={() => {
          setSearchExpanded(true);
          setTimeout(() => navbarInputRef.current?.focus(), 50);
        }}
        className="p-2 text-zinc-400 hover:text-white transition"
        aria-label="Search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    ) : (
      <div className="relative w-64 sm:w-72">
        <input
          ref={navbarInputRef}
          type="text"
          value={navbarSearchTerm}
          onChange={(e) => setNavbarSearchTerm(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
          autoFocus
        />
        <span className="absolute left-4 top-3.5 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {navbarResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
            {navbarResults.map((item, index) => (
              <Link
                key={`${item.type}-${item.bdl_id}`}
                to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                onClick={() => {
                  setNavbarSearchTerm("");
                  setNavbarResults([]);
                  setSearchExpanded(false);
                }}
              >
                {item.type === "team" ? (
                  <>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-zinc-500 text-sm"> • Team</span>
                  </>
                ) : (
                  <>
                    {item.first_name} {item.last_name} • {item.position_abbreviation}
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Desktop Nav Links */}
  <div className="hidden md:flex items-center gap-8 text-sm font-medium">
    {/* TEAMS */}
    <div className="relative" ref={teamsRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
        className="hover:text-red-400 transition"
      >
        TEAMS
      </button>
      {openDropdown === "teams" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
          <Link to="/Standings" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standings</Link>
          <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="relative" ref={leadersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
        className="hover:text-red-400 transition"
      >
        LEADERS
      </button>
      {openDropdown === "leaders" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
          <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div className="relative" ref={playersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
        className="hover:text-red-400 transition"
      >
        PLAYERS
      </button>
      {openDropdown === "players" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
          <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
        </div>
      )}
    </div>
  </div>

  {/* Mobile Hamburger Button */}
  <button
    className="md:hidden p-2 text-zinc-400 hover:text-white"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    aria-label="Menu"
  >
    {mobileMenuOpen ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </button>
</div>

        </div>

{/* Mobile Menu - collapsible sections */}
{mobileMenuOpen && (
  <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
    
    {/* TEAMS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        TEAMS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "teams" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "teams" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            All Teams
          </Link>
          <Link to="/Standings" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Standings
          </Link>
          <Link to="/Schedule" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Schedule
          </Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        LEADERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "leaders" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "leaders" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Season Leaders
          </Link>
          <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Passing
          </Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Rushing
          </Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Receiving
          </Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div>
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "players" ? null : "players")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        PLAYERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "players" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "players" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Stats
          </Link>
          <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Comparison
          </Link>
         </div>
      )}
    </div>

  </div>
)}
      </nav>

      <div className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6">
           {/*<Link to="/teams" className="inline-flex items-center text-red-400 hover:text-red-500 mb-8">
            ← Back to All Teams
          </Link>*/}

          {/* TOP SECTION */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl p-10 mb-12 text-center">
            {/*<div className="w-40 h-40 bg-gradient-to-br from-red-500 via-white to-blue-500 rounded-2xl mx-auto mb-6 flex items-center justify-center text-7xl font-black border-4 border-white/30 shadow-xl">
              {team.abbreviation}
            </div>*/}
            <h1 className="text-6xl font-black mb-3">{team.full_name || team.name}</h1>
            <p className="text-xl text-zinc-400">
              {team.conference} • {team.division} • {team.stadium_name}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto mt-10">
              <div className="bg-zinc-800 p-5 rounded-2xl">
                <div className="text-sm text-zinc-500">OVERALL</div>
                <div className="text-3xl font-bold mt-1">{record?.overall_record || "0-0-0"}</div>
              </div>
              <div className="bg-zinc-800 p-5 rounded-2xl">
                <div className="text-sm text-zinc-500">CONFERENCE</div>
                <div className="text-3xl font-bold mt-1">{record?.conference_record || "—"}</div>
              </div>
              <div className="bg-zinc-800 p-5 rounded-2xl">
                <div className="text-sm text-zinc-500">DIVISION</div>
                <div className="text-3xl font-bold mt-1">{record?.division_record || "—"}</div>
              </div>
              <div className={`p-5 rounded-2xl ${record?.win_streak > 0 ? "bg-green-900/50" : record?.win_streak < 0 ? "bg-red-900/50" : "bg-zinc-800"}`}>
                <div className="text-sm text-zinc-500">STREAK</div>
                <div className="text-3xl font-bold mt-1">
                  {record?.win_streak > 0 ? `W${record.win_streak}` : record?.win_streak < 0 ? `L${Math.abs(record.win_streak)}` : "—"}
                </div>
              </div>
              {record?.playoff_seed && (
                <div className="bg-blue-300/30 p-5 rounded-2xl">
                  <div className="text-sm text-zinc-500">PLAYOFF SEED</div>
                  <div className="text-3xl font-bold mt-1 text-blue-900">#{record.playoff_seed}</div>
                </div>
              )}
            </div>
          </div>

          {/* LEADERS */}
          <div className="mb-16">
            <h2 className="text-4xl font-black text-center mb-10">TEAM LEADERS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "Passing Leader", data: leaders.pass, stat: "passing_yards" },
                { title: "Rushing Leader", data: leaders.rush, stat: "rushing_yards" },
                { title: "Receiving Leader", data: leaders.rec, stat: "receiving_yards" },
                { title: "Tackles Leader", data: leaders.tackles, stat: "solo_tackles" },
              ].map(({ title, data, stat }) => (
                <div key={title} className="bg-zinc-900 rounded-3xl p-8 text-center hover:border-red-500 border border-zinc-800 transition">
                  <h3 className="text-red-400 font-bold mb-6">{title}</h3>
                  {data ? (
                    <Link to={`/player/${data.player_id}`} className="block hover:scale-105 transition">
                      <div className="text-2xl font-bold mb-3">{data.name}</div>
                      <div className="text-6xl font-black text-white mb-1">{data[stat] || 0}</div>
                      <div className="text-sm text-zinc-500">{stat.includes("yards") ? "YARDS" : "TACKLES"}</div>
                    </Link>
                  ) : (
                    <p className="text-zinc-500 py-12">No leader data yet</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* TABS */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex border-b border-zinc-700">
              {["stats", "roster", "schedule"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-6 text-xl font-bold transition ${
                    activeTab === tab ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {tab === "stats" ? "TEAM STATS" : tab === "roster" ? "ACTIVE ROSTER" : "SCHEDULE"}
                </button>
              ))}
            </div>

            {/* STATS TAB */}
            {activeTab === "stats" && teamStats && (
              <div className="p-10">
                <h3 className="text-3xl font-bold text-center mb-10">2026 Season Stats</h3>
                <div className="grid lg:grid-cols-2 gap-12">
                  <div>
                    <h4 className="text-2xl font-bold mb-6 text-green-500 text-center">TEAM</h4>
                    {Object.entries(teamStats)
                      .filter(([k]) => !k.startsWith("opp_") && !["id","season","team_id","created_at","updated_at","games_played","season_type"].includes(k))
                      .map(([key, value]) => (
                        <div key={key} className="flex justify-between py-4 border-b border-zinc-700">
                          <span className="capitalize">{key.replace(/_/g, " ")}</span>
                          <span className="font-mono font-bold">{value ?? "—"}</span>
                        </div>
                      ))}
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold mb-6 text-red-500 text-center">OPPONENT</h4>
                    {Object.entries(teamStats)
                      .filter(([k]) => k.startsWith("opp_"))
                      .map(([key, value]) => (
                        <div key={key} className="flex justify-between py-4 border-b border-zinc-700">
                          <span className="capitalize">{key.replace("opp_", "").replace(/_/g, " ")}</span>
                          <span className="font-mono font-bold text-red-400">{value ?? "—"}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* ROSTER TAB */}
            {activeTab === "roster" && (
  <div className="p-10">
    <h3 className="text-3xl font-bold text-center mb-10">Active Roster</h3>
    {loadingRoster ? (
      <div className="text-center py-20 text-zinc-500">Loading roster...</div>
    ) : (
      <div className="space-y-3">
        {/* Header */}
        <div className="grid grid-cols-4 gap-4 px-6 py-3 text-sm text-zinc-500 font-medium border-b border-zinc-700">
          <div>POS</div>
          <div>#</div>
          <div>PLAYER</div>
          <div className="text-right">STATUS</div>
        </div>

        {/* Rows */}
        {sortedRoster.map((p) => (
          <div
            key={p.bdl_id}
            className="bg-zinc-800 rounded-2xl px-6 py-4 grid grid-cols-4 gap-4 items-center hover:bg-zinc-700/70 transition"
          >
            <div className="font-mono text-zinc-300">
              {p.position || p.position_abbreviation}
            </div>
            <div className="text-zinc-400">
              {p.jersey_number ? `#${p.jersey_number}` : "—"}
            </div>
            <div>
              <Link
                to={`/player/${p.bdl_id}`}
                className="font-medium hover:text-red-400 transition"
              >
                {p.name || `${p.first_name} ${p.last_name}`}
              </Link>
            </div>
            <div className="text-right">
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                  p.status === "Active" || p.status === "Healthy"
                    ? "bg-green-900/40 text-green-400"
                    : p.status
                    ? "bg-yellow-900/40 text-yellow-400"
                    : "bg-zinc-700 text-zinc-400"
                }`}
              >
                {p.status || "—"}
              </span>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

            {/* SCHEDULE TAB */}
            {activeTab === "schedule" && (
  <div className="p-10">
    <h3 className="text-3xl font-bold text-center mb-10">2026 Schedule</h3>
    {loadingSchedule ? (
      <div className="text-center py-20 text-zinc-500">Loading schedule...</div>
    ) : schedule.length === 0 ? (
      <div className="text-center py-20 text-zinc-500">No games found</div>
    ) : (
      <div className="space-y-4">
        {(() => {
          // Create a map of week → game
          const gameByWeek = {};
          schedule.forEach((g) => {
            gameByWeek[g.week] = g;
          });

          // Find min and max week from the data
          const weeks = schedule.map((g) => g.week).filter(Boolean);
          const minWeek = Math.min(...weeks);
          const maxWeek = Math.max(...weeks);

          // Build full list including bye weeks
          const fullSchedule = [];
          for (let week = minWeek; week <= maxWeek; week++) {
            if (gameByWeek[week]) {
              fullSchedule.push(gameByWeek[week]);
            } else {
              // Insert bye week
              fullSchedule.push({
                id: `bye-${week}`,
                week,
                is_bye: true,
              });
            }
          }

          return fullSchedule.map((game) => {
            if (game.is_bye) {
              return (
                <div
                  key={game.id}
                  className="bg-zinc-800/60 rounded-2xl p-6 flex items-center justify-between border border-dashed border-zinc-600"
                >
                  <div className="flex items-center gap-6">
                    <div className="text-zinc-400 w-20">Week {game.week}</div>
                    <div className="font-bold text-lg text-zinc-400">BYE WEEK</div>
                  </div>
                  <div className="text-zinc-500 text-sm">—</div>
                </div>
              );
            }

            const isFinal = game.status === "Final" || game.status === "Final/OT";
            const dateStr = game.game_date
              ? new Date(game.game_date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })
              : "TBD";

            const isWin = isFinal && game.team_score > game.opponent_score;
            const isLoss = isFinal && game.team_score < game.opponent_score;

            return (
              <div
                key={game.id}
                className="bg-zinc-800 rounded-2xl p-6 flex items-center justify-between"
              >
                <div className="flex items-center gap-6">
                  <div className="text-zinc-400 w-20">Week {game.week}</div>
                  <div>
                    <div className="font-bold text-lg">
                      {game.is_home ? "vs" : "@"} {game.opponent_abbr}
                    </div>
                    <div className="text-sm text-zinc-500">{dateStr}</div>
                  </div>
                </div>

                <div className="text-right">
                  {isFinal ? (
                    <div className="text-2xl font-black text-white flex items-center gap-2 justify-end">
                      {isWin && <span className="text-green-400">W</span>}
                      {isLoss && <span className="text-red-400">L</span>}
                      <span>
                        {game.team_score} - {game.opponent_score}
                      </span>
                    </div>
                  ) : (
                    <div className="text-zinc-400">Upcoming</div>
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>
    )}
  </div>
)}

          </div>
        </div>
      </div>

      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default TeamPage;